<template>
  <div
    class="sphere"
    :class="stateClass"
    :style="sphereStyle"
    role="img"
    :aria-label="ariaLabel"
    @transitionend="onTransitionEnd"
  >
    <!-- Background twinkles -->
    <div class="twinkles" aria-hidden="true">
      <i v-for="n in 18" :key="'tw'+n" class="tw"></i>
    </div>

    <!-- Outer aura -->
    <div class="aura"></div>

    <!-- 🔥 Triple chroma ring with lava motion + scanning arc -->
    <div class="chroma-ring" aria-hidden="true">
      <i class="layer l1"></i>
      <i class="layer l2"></i>
      <i class="layer l3"></i>
      <i class="arc"></i>
    </div>

    <!-- Soft bloom ring -->
    <div class="ring"></div>

    <!-- Lens flare -->
    <div class="flare" aria-hidden="true"></div>

    <!-- Orbiters (thinking) -->
    <div class="orbiters" aria-hidden="true">
      <span class="orb o1"></span>
      <span class="orb o2"></span>
      <span class="orb o3"></span>
      <span class="orb o4"></span>
    </div>

    <!-- Radiating waves (always on, intensity varies) -->
    <div class="waves" aria-hidden="true">
      <span class="wave w1"></span>
      <span class="wave w2"></span>
      <span class="wave w3"></span>
    </div>

    <!-- Particle bursts on state change -->
    <div class="particles" ref="particles" aria-hidden="true"></div>

    <!-- Core -->
    <div class="core">
      <div class="sheen" aria-hidden="true"></div>


      <div class="voice-bars" aria-hidden="true">
        <i
          v-for="(h, i) in bars"
          :key="i"
          class="bar"
          :style="{'--bh': h + '%', '--delay': (i*90)+'ms'}"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GlowingSphereV2",
  props: {
    size: { type: Number, default: 320 },
    listening: Boolean,
    thinking: Boolean,
    responding: Boolean,
    /**
     * Live mic/tts energy 0..1 — drives visuals continuously
     * Supply mic RMS while listening, and TTS energy while responding.
     * When idle/thinking, you can still feed low values for life.
     */
    level: { type: Number, default: 0 },
    /** Global multiplier for animation speeds (0.25..3) */
    speed: { type: Number, default: 1 },
  },
  data() {
    return {
      bars: [10, 16, 24, 16, 10],
      rafId: null,
      lastTime: 0,
      phaseA: Math.random() * Math.PI * 2,
      phaseB: Math.random() * Math.PI * 2,
      prevStateKey: "",
      tickCb: null,
    };
  },
  computed: {
    stateClass() {
      return {
        listening: this.listening,
        thinking: this.thinking,
        responding: this.responding,
        idle: !this.listening && !this.thinking && !this.responding,
        active: this.listening || this.thinking || this.responding
      };
    },
    sphereStyle() {
      const lvl = Math.max(0, Math.min(1, this.level || 0));
      const spd = Math.max(0.25, Math.min(3, this.speed || 1));

      // Global energy & glow scaling
      const energy = (this.listening || this.responding)
        ? 0.4 + lvl * 0.6
        : 0.15 + lvl * 0.35;

      // Triple ring rotation + lava flow timings (seconds per cycle)
      const ringRot =
        this.listening ? 5.2 :
        this.responding ? 9.2 :
        this.thinking ? 13.2 : 21.5;

      const flow1 =
        this.listening ? 5.8 :
        this.responding ? 8.6 :
        this.thinking ? 12.4 : 18.0;

      const flow2 =
        this.listening ? 7.0 :
        this.responding ? 10.2 :
        this.thinking ? 14.8 : 22.0;

      const flow3 =
        this.listening ? 9.5 :
        this.responding ? 13.6 :
        this.thinking ? 18.5 : 26.5;

      // Wave timing & opacity
      const waveOpacity = this.listening ? 1 : this.responding ? 0.9 : Math.min(0.55, 0.1 + lvl * 0.5);
      const waveSpeed = this.listening ? 1.25 : this.responding ? 1.9 : 2.8;

      // Bars opacity always > 0 (life in idle), highest in speaking
      const barsOpacity = this.listening ? 0.98 : this.responding ? 0.9 : Math.min(0.65, 0.2 + lvl * 0.6);

      return {
        width: this.size + "px",
        height: this.size + "px",
        "--size": this.size + "px",
        "--level": String(lvl),
        "--energy": String(energy),
        "--glow": `${Math.round(22 + 40 * energy)}px`,
        "--spd": String(spd),
        "--ring-rot": `${ringRot}s`,
        "--flow1": `${flow1}s`,
        "--flow2": `${flow2}s`,
        "--flow3": `${flow3}s`,
        "--waveOpacity": waveOpacity,
        "--waveSpeed": `${waveSpeed}s`,
        "--barsOpacity": barsOpacity,
      };
    },
    ariaLabel() {
      if (this.listening) return "Listening sphere reacting to your voice";
      if (this.thinking) return "Thinking sphere with orbiting particles";
      if (this.responding) return "Responding sphere reacting to its own voice";
      return "Idle sphere softly animated";
    },
    stateKey() {
      return `${this.listening?'L':''}${this.thinking?'T':''}${this.responding?'R':''}`;
    }
  },
  watch: {
    stateKey(n, p) {
      if (p !== undefined && n !== p) {
        if (this.responding) this.spawnBurst('teal');
        else if (this.listening) this.spawnBurst('cyan');
        else if (this.thinking) this.spawnBurst('violet');
        else this.spawnBurst('soft');
      }
      this.prevStateKey = n;
    }
  },
  mounted() {
    this.prevStateKey = this.stateKey;
    this.tickCb = (ts) => this.tick(ts);
    this.tick(performance.now());
  },
  beforeDestroy() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
  },
  methods: {
    tick(t) {
      const dt = Math.min(64, t - this.lastTime);
      this.lastTime = t;

      // Normalized level drives everything (keep some life even if 0)
      const lvl = Math.max(0, Math.min(1, this.level || 0));
      const spd = (this.speed || 1);

      // Phases for organic motion
      const baseA = this.listening ? 3.0 : this.responding ? 2.1 : this.thinking ? 1.5 : 1.1;
      const baseB = this.listening ? 2.3 : this.responding ? 1.7 : this.thinking ? 1.3 : 0.9;
      this.phaseA += (dt / 1000) * baseA * spd;
      this.phaseB += (dt / 1000) * baseB * spd;

      // Amplitude model: idle<thinking<responding<listening
      const amp =
        this.listening ? 26 + lvl * 78 :
        this.responding ? 18 + lvl * 62 :
        this.thinking ? 12 + lvl * 34 :
        10 + lvl * 28;

      // Voice bars — reactive in all states
      const next = this.bars.map((h, i) => {
        const n1 = Math.sin(this.phaseA * (1.0 + i*0.08) + i*0.6);
        const n2 = Math.cos(this.phaseB * (1.4 + i*0.05) - i*0.5);
        const noise = (n1 * 0.6 + n2 * 0.4 + 1) * 0.5; // 0..1
        // small beat from level to keep idle alive
        const beat = 0.35 + lvl * 0.65;
        const target = Math.max(6, amp * (0.45 + 0.55 * noise) * beat);
        return h + (target - h) * 0.22;
      });
      this.bars = next;

      // Request next frame
      this.rafId = requestAnimationFrame(this.tickCb);
    },

    spawnBurst(theme = 'soft', count = 12) {
      const host = this.$refs.particles;
      if (!host) return;
      const colors = {
        teal: ['#8cffdf', '#00e0b8', '#00a896'],
        cyan: ['#e8ffff', '#8be9ff', '#3fd0ff'],
        violet: ['#d6ccff', '#a79bff', '#7c6cff'],
        soft: ['#f0fbff', '#c9f5ff', '#bde7ff']
      }[theme];

      for (let i = 0; i < count; i++) {
        const el = document.createElement('i');
        el.className = 'p';
        const ang = (i / count) * Math.PI * 2 + (Math.random() * 0.6);
        const dist = 28 + Math.random() * 42;
        const dur = 560 + Math.random() * 560;

        el.style.setProperty('--tx', Math.cos(ang) * dist + 'px');
        el.style.setProperty('--ty', Math.sin(ang) * dist + 'px');
        el.style.setProperty('--dur', dur + 'ms');
        el.style.setProperty('--sz', (3 + Math.random() * 6) + 'px');
        el.style.background = `radial-gradient(circle at 30% 30%, #fff, ${colors[i%colors.length]})`;
        host.appendChild(el);
        el.addEventListener('animationend', () => el.remove());
      }
    },

    onTransitionEnd() {}
  }
};
</script>

<style scoped>
:root {
  --c1: #8be9ff;
  --c2: #3fd0ff;
  --c3: #2a6fff;
  --accentA: #7c6cff;
  --accentB: #00e0ff;
  --accentC: #00e0b8;
}

/* Base */
.sphere {
  --size: 320px;
  --level: 0;
  --energy: 0.5; /* 0..1, drives glow/intensity */
  --glow: 68px;
  --spd: 1;

  --ring-rot: 20s;
  --flow1: 18s;
  --flow2: 22s;
  --flow3: 26s;

  --waveOpacity: .35;
  --waveSpeed: 2.8s;
  --barsOpacity: .6;

  position: relative;
  border-radius: 9999px;
  display: grid;
  place-items: center;
  isolation: isolate;
  filter:
    drop-shadow(0 0 calc(var(--glow)) rgba(77,220,255, .45))
    drop-shadow(0 8px 28px rgba(0,0,0,.35));
  will-change: transform, filter;
  transition: filter 280ms ease, transform 280ms ease;
}

/* Idle vs active float */
.sphere.idle {
  animation:
    floatY calc(6.5s/var(--spd)) ease-in-out infinite,
    tilt   calc(9s/var(--spd))   ease-in-out infinite;
}
.sphere.active {
  animation:
    floatY calc(4.6s/var(--spd)) ease-in-out infinite,
    tilt   calc(7.0s/var(--spd)) ease-in-out infinite;
}

/* Twinkles */
.twinkles { position: absolute; inset: -18%; pointer-events: none; filter: blur(0.25px); z-index: 0; }
.tw { position: absolute; width: 3px; height: 3px; border-radius: 50%; background: radial-gradient(circle, #fff, transparent 72%); opacity: 0; animation: twinkle calc(3.2s/var(--spd)) ease-in-out infinite; transform: translateZ(-1px); }
.tw:nth-child(odd){ animation-delay: 1.1s }
.tw:nth-child(1){ top:8%; left:22% } .tw:nth-child(2){ top:16%; right:18% } .tw:nth-child(3){ bottom:12%; left:14% }
.tw:nth-child(4){ top:26%; left:6% } .tw:nth-child(5){ bottom:24%; right:10% } .tw:nth-child(6){ top:8%; right:46% }
.tw:nth-child(7){ bottom:8%; left:44% } .tw:nth-child(8){ top:42%; left:86% } .tw:nth-child(9){ top:72%; left:74% }
.tw:nth-child(10){ top:64%; left:8% } .tw:nth-child(11){ top:32%; left:36% } .tw:nth-child(12){ top:18%; left:64% }
.tw:nth-child(13){ bottom:28%; left:62% } .tw:nth-child(14){ top:52%; left:16% } .tw:nth-child(15){ top:68%; left:28% }
.tw:nth-child(16){ top:34%; left:78% } .tw:nth-child(17){ top:26%; left:52% } .tw:nth-child(18){ bottom:18%; left:32% }

/* Aura / ring / core */
.aura, .ring, .core { border-radius: 9999px; }

.core {
  width: 400px; height: 400px; position: relative; overflow: hidden;
  background:
    radial-gradient(circle at 42% 32%, var(--c1) 0%, var(--c2) 38%, var(--c3) 100%),
    conic-gradient(from 0deg, rgba(255,255,255,.05), rgba(255,255,255,0) 60%);
  box-shadow:
    inset 0 0 calc(18px + 18px * var(--energy)) rgba(255,255,255,.22),
    0 0 calc(8px + 22px * var(--energy)) rgba(124,108,255,.28);
  transform: translateZ(0); z-index: 5;
  transition: background 480ms ease;
  opacity: 0.9;
}
.aura {
  position: absolute; inset: -12%;
  background:
    radial-gradient(closest-side, rgba(63,208,255,.24), transparent 70%),
    radial-gradient(circle at 30% 20%, rgba(124,108,255,.20), transparent 55%),
    radial-gradient(circle at 72% 76%, rgba(0,224,184,.18), transparent 55%);
  filter: blur(8px); pointer-events: none; z-index: 1;
}

/* === Triple Chromatic Lava Ring + Arc === */
.chroma-ring {
  position: absolute; inset: -6%;
  border-radius: 9999px;
  -webkit-mask: radial-gradient(closest-side, transparent 62%, #000 63%);
          mask: radial-gradient(closest-side, transparent 62%, #000 63%);
  overflow: hidden;
  z-index: 2;
}
.chroma-ring .layer {
  position: absolute; inset: -14%;
  border-radius: 9999px;
  mix-blend-mode: screen;
  filter: blur(6px) contrast(1.05);
  opacity: .3;
}
.chroma-ring .l1 {
  background:
    radial-gradient(120% 120% at 30% 40%, rgba(0, 225, 255, 0.167), transparent 30%),
    conic-gradient(from 0deg, #00e1ff38, #7d6cff1e, #00e0b78d, #00e1ff89);
  animation:
    ringRotate var(--ring-rot) linear infinite,
    lava1 var(--flow1) ease-in-out infinite alternate,
    hueShift 8s linear infinite;
  opacity: .02;
}


/* Scanning arc that reacts with energy */
.chroma-ring .arc {
  position: absolute; inset: -6%;
  border-radius: 9999px;
  background:
    conic-gradient(
      from 0deg,
      rgba(255,255,255,0) 0 70%,
      rgba(255,255,255, calc(.25 + .55*var(--energy))) 75%,
      rgba(255,255,255,0) 85% 100%
    );
  -webkit-mask: radial-gradient(closest-side, transparent 62%, #000 63%);
          mask: radial-gradient(closest-side, transparent 62%, #000 63%);
  mix-blend-mode: screen;
  filter: blur(2px);
  animation: ringRotate calc(var(--ring-rot) * 0.65) linear infinite;
  opacity: .3;
}

/* Soft bloom ring behind chroma */
.ring {
  position: absolute; width: 100%; height: 100%;
  background: radial-gradient(closest-side, rgba(63,208,255,.16), transparent 70%);
  box-shadow:
    0 0 0 1px rgba(63,208,255,.14) inset,
    0 0 24px rgba(63,208,255,.22);
  pointer-events: none; z-index: 1;
}

/* Flare */
.flare {
  position: absolute; border-radius: 9999px; inset: 0;
  background:
    radial-gradient(100% 60% at 20% 20%, rgba(255,255,255,.22), transparent 60%),
    radial-gradient(80% 50% at 78% 72%, rgba(255,255,255,.15), transparent 60%);
  mix-blend-mode: screen; filter: blur(2px);
  animation: flarePulse calc(5.4s/var(--spd)) ease-in-out infinite;
  z-index: 3;
}

/* Core */
.core {
  width: 58%; height: 58%; position: relative; overflow: hidden;
  background:
    radial-gradient(circle at 42% 32%, var(--c1) 0%, var(--c2) 38%, var(--c3) 100%),
    conic-gradient(from 0deg, rgba(255,255,255,.05), rgba(255,255,255,0) 60%);
  box-shadow:
    inset 0 0 calc(18px + 18px * var(--energy)) rgba(255,255,255,.22),
    0 0 calc(8px + 22px * var(--energy)) rgba(124,108,255,.28);
  transform: translateZ(0); z-index: 5;
}
.sheen {
  position: absolute; inset: -60%;
  background: conic-gradient(from 0deg, rgba(255,255,255,0.14), rgba(255,255,255,0) 20% 80%, rgba(255,255,255,0.14));
  mix-blend-mode: screen; animation: spin calc(22s/var(--spd)) linear infinite; pointer-events: none;
}

/* Orbiters (thinking) */
.orbiters { position: absolute; width: 88%; height: 88%; pointer-events: none; z-index: 6; }
.orb { position: absolute; width: 10%; height: 10%; border-radius: 9999px; background: radial-gradient(circle at 35% 35%, rgba(72, 33, 85, 0.064)3a169, #b3e8ff75 60%, transparent 50%); filter: drop-shadow(0 0 10px rgba(140,255,223,.6)); opacity: 0; transform-origin: 50% 50%; }
.thinking .orb { opacity: 1; }
.thinking .o1 { animation: orbit calc(6.5s/var(--spd)) linear infinite; }
.thinking .o2 { animation: orbit calc(9s/var(--spd)) linear infinite reverse; }
.thinking .o3 { animation: orbit calc(11s/var(--spd)) linear infinite; }
.thinking .o4 { animation: orbit calc(13s/var(--spd)) linear infinite reverse; }

/* Waves (always running, opacity via var) */
.waves { position: absolute; inset: 0; pointer-events: none; z-index: 5; }
.wave {
  position: absolute; left: 50%; top: 50%; width: 50%; height: 50%;
  border-radius: 9999px; transform: translate(-50%, -50%);
  box-shadow: 0 0 0 2px rgba(0,224,255,.28) inset;
  opacity: var(--waveOpacity);
  animation: radiate var(--waveSpeed) ease-out infinite;
}
.w2 { animation-delay: .2s; }
.w3 { animation-delay: .9s; }

/* Voice bars (reactive in all states) */
.voice-bars {
  position: absolute; inset: 0; display: grid; grid-template-columns: repeat(5, 1fr); align-items: end;
  padding: 23%; gap: 6%; opacity: var(--barsOpacity);
}
.bar {
  display: block; width: 100%; border-radius: 8px;
  background: linear-gradient(180deg, #fff, #00e0ff 45%, #00a896 100%);
  box-shadow: 0 6px 16px rgba(0, 224, 255, .28);
  height: var(--bh, 22%);
  animation: barJitter calc(600ms/var(--spd)) ease-in-out infinite alternate;
  animation-delay: var(--delay, 0ms);
}

/* Particles */
.particles { position: absolute; inset: 0; pointer-events: none; z-index: 7; }
.particles .p { position: absolute; left: 50%; top: 50%; width: var(--sz); height: var(--sz); border-radius: 9999px; transform: translate(-50%, -50%); animation: burst var(--dur) ease-out forwards, fade var(--dur) ease-out forwards; }

/* State colorways (core bias) */
.listening .core {
  background:
    conic-gradient(from 0deg, var(--accentB), var(--accentA), var(--accentC), var(--accentB));
  animation: hue 3s linear infinite;
}
.thinking .core {
  background:
    radial-gradient(circle, #5a7cff, #3a2bd6 60%, #1a124a 100%),
    conic-gradient(from 0deg, rgba(255,255,255,.05), rgba(255,255,255,0) 60%);
}
.responding .core {
  background:
    radial-gradient(circle, #8cffdf, #00e0b8 45%, #00a896 100%),
    conic-gradient(from 0deg, rgba(255,255,255,.05), rgba(255,255,255,0) 60%);
}

/* Animations */
@keyframes floatY { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
@keyframes tilt   { 0%, 100% { transform: rotateX(0deg) rotateZ(0deg) } 50% { transform: rotateX(2deg) rotateZ(-1.5deg) } }
@keyframes twinkle { 0%, 100% { opacity: 0 } 50% { opacity: .7 } }
@keyframes radiate { 0% { transform: translate(-50%, -50%) scale(.9); opacity: 0; } 8% { opacity: .35; } 100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; } }
@keyframes barJitter { from { transform: scaleY(.85); } to { transform: scaleY(1.15); } }
@keyframes flarePulse { 0%, 100% { opacity: .35; filter: blur(1px) } 50% { opacity: .65; filter: blur(2px) } }
@keyframes orbit { 0% { transform: rotate(0deg) translateY(-44%) rotate(0deg) } 100% { transform: rotate(360deg) translateY(-44%) rotate(-360deg) } }
@keyframes burst { 0% { transform: translate(-50%, -50%) translate(0,0) scale(.8) } 100% { transform: translate(-50%, -50%) translate(var(--tx), var(--ty)) scale(1) } }
@keyframes fade  { 0% { opacity: 1 } 100% { opacity: 0 } }
@keyframes spin  { to { transform: rotate(360deg) } }
@keyframes hue   { to { filter: hue-rotate(360deg) } }

/* Ring rotations */
@keyframes ringRotate { to { transform: rotate(360deg) } }
@keyframes ringRotateReverse { to { transform: rotate(-360deg) } }

/* Lava-like gradient flows */
@keyframes lava1 {
  0%   { background-position: 0% 0%, 0 0; background-size: 100% 100%, 100% 100%; }
  50%  { background-position: 40% 60%, 0 0; background-size: 125% 115%, 100% 100%; }
  100% { background-position: 0% 0%, 0 0; background-size: 100% 100%, 100% 100%; }
}
@keyframes lava2 {
  0%   { background-position: 60% 20%, 0 0; background-size: 110% 125%, 100% 100%; }
  50%  { background-position: 20% 80%, 0 0; background-size: 135% 110%, 100% 100%; }
  100% { background-position: 60% 20%, 0 0; background-size: 110% 125%, 100% 100%; }
}
@keyframes lava3 {
  0%   { background-position: 30% 70%, 0 0; background-size: 115% 110%, 100% 100%; }
  50%  { background-position: 80% 30%, 0 0; background-size: 145% 120%, 100% 100%; }
  100% { background-position: 30% 70%, 0 0; background-size: 115% 110%, 100% 100%; }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .sphere, .core, .aura, .ring, .sheen, .orbiters .orb, .waves .wave, .voice-bars .bar, .flare, .tw, .particles .p, .chroma-ring .layer, .chroma-ring .arc {
    animation: none !important;
  }
}
</style>
